<template>
  <div class="wrapper">
    <ShopScreenTopBar
      :payee-id="payeeId"
      :catalog-id="catalogId"
      screen="kitchen"
      :now="now"
      :freshness="dataFreshness"
      :pending="pending"
      @refresh="requestOrders"
    />
    <div class="content">
      <div class="d-flex justify-content-end my-3">
        <KitchenControls
          :show-grouping="orgs.length > 0"
          :group-by-org.sync="groupByOrg"
          :selected-group-filter.sync="selectedGroupFilter"
          :groups="groups"
          @collapse-all="collapseAll"
          @expand-all="expandAll"
        />
      </div>
      <div class="full-height">
        <div v-if="aggregatedOrders.length === 0">
          No orders found
        </div>

        <div
          v-for="org in orgGroupProducts"
          :key="org.id"
          class="org-section"
        >
          <OrganizationHeader
            :org-name="org.name"
            :comment="org.comment"
            :orders-comment="org.ordersComment"
            :week-day-name="weekDayName"
          />

          <div
            v-for="group in org.groups"
            :key="group.groupCode"
            class="group-section"
          >
            <div
              v-if="group.groupCode"
              class="group-header clickable"
              @click="toggleGroup(org.id, group.groupCode)"
            >
              <div class="group-name">
                {{ group.groupName }}
              </div>
              <div class="d-flex align-items-center">
                <div class="group-number ml-2">
                  <div class="group-number-label">
                    do wydania
                  </div>
                  <div class="group-number-value">
                    {{ group.totalLeftToDeliver }}
                  </div>
                </div>
                <div class="group-number">
                  <div class="group-number-label">
                    Zamówiono
                  </div>
                  <div class="group-number-value">
                    {{ group.totalCount }}
                  </div>
                </div>
                <div>
                  <div class="group-number d-flex justify-content-end align-items-center">
                    <div class="group-number-label pr-2">
                      wczoraj
                    </div>
                    <div class="group-number-value-past">
                      {{ group.totalPreviousDayCount }}
                    </div>
                  </div>
                  <div class="group-number mt-1 d-flex justify-content-end align-items-center dimmed">
                    <div class="group-number-label pr-2">
                      zeszły tydzień
                    </div>
                    <div class="group-number-value">
                      {{ group.totalLastWeekCount }}
                    </div>
                  </div>
                </div>
                <div class="group-chevron-spacer">
                  <i :class="isGroupExpanded(org.id, group.groupCode) ? 'fas fa-chevron-up' : 'fas fa-chevron-down'" />
                </div>
              </div>
            </div>
            <div v-if="isGroupExpanded(org.id, group.groupCode)">
              <KitchenProductSummary
                v-for="product in group.products"
                :key="`${product.productId}-${product.menuId || ''}`"
                :product="product"
                :expand-all="expandAllState"
                :diets="diets"
                :groups="groups"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { mapActions, mapGetters } from 'vuex';
import ShopScreenTopBar from '@/components/shop/today/ShopScreenTopBar';
import KitchenProductSummary from './KitchenProductSummary';
import KitchenControls from './components/KitchenControls';
import OrganizationHeader from './components/OrganizationHeader';
import {
  calculatePeriodCount,
  processIngredients,
  mergeIngredients,
  mergeExistingIngredient,
  createProductEntry,
  createSubProduct,
  aggregateProductCounts,
} from './utils/orderAggregation';
import { transformOrdersToProducts, resolveProductIds } from './utils/productTransform';
import { calculateDataFreshness, getWeekdayName } from './utils/dateFormatting';
import { getProductsByOrgView } from './utils/organizationGrouping';
import { fetchAllOrders, fetchCatalogMenuData } from './utils/dataFetching';
import { assignDietColors } from './utils/dietColors';

export default {
  props: {
    payeeId: String,
    catalogId: String,
  },
  data: () => ({
    search: '',
    list: [],
    orgs: [],
    orders: {},
    lastWeekOrders: {},
    previousDayOrders: {},
    dataFrom: null,
    now: null,
    pending: false,
    menuNodes: [],
    meals: null,
    ingredients: null,
    listings: [],
    deliveryPlan: {},
    catalog: null,
    weekDayName: '',
    groupByOrg: false,
    selectedGroupFilter: [],
    orgCommentPropId: null,
    expandAllState: null,
    dietColors: [],
    payersById: {},
    expandedGroups: {},
    loggedFirstGroup: null,
  }),
  components: {
    KitchenProductSummary,
    ShopScreenTopBar,
    KitchenControls,
    OrganizationHeader,
  },
  computed: {
    ...mapGetters(['properties', 'groups']),
    diets() {
      const rawDiets = this.catalog.subCatalogNodes.filter((x) => x.subCatalogId === this.catalog.metadata.dietId);
      return assignDietColors(rawDiets);
    },
    productsByOrg() {
      const result = getProductsByOrgView(
        this.groupByOrg,
        this.orderedProducts,
        this.lastWeekOrderedProducts,
        this.previousDayOrderedProducts,
        this.orgs,
        this.orders,
        this.orgCommentPropId,
        this.getAggregatedOrders.bind(this),
        this.selectedGroupFilter,
      );

      // If group filter is active and not grouping by org, only show products with counts in selected groups
      if (this.selectedGroupFilter.length > 0 && !this.groupByOrg) {
        return result
          .map((org) => ({
            ...org,
            orders: org.orders
              .map((menu) => ({
                ...menu,
                products: menu.products.filter(
                  (product) =>
                    product.countsByGroup
                    && Object.keys(product.countsByGroup).some((groupCode) =>
                      this.selectedGroupFilter.includes(groupCode)),
                ),
              }))
              .filter((menu) => menu.products.length > 0), // Remove empty menus
          }))
          .filter((org) => org.orders.length > 0); // Remove empty orgs
      }

      return result;
    },
    orgGroupProducts() {
      // If no filter, show all products without grouping by group
      if (this.selectedGroupFilter.length === 0) {
        return this.productsByOrg.map((org) => ({
          ...org,
          groups: org.orders.map((menu) => ({
            groupCode: null,
            groupName: menu.name,
            products: menu.products.map((product) => ({
              ...product,
              menuId: menu.nodeId,
              menuName: menu.name,
            })),
          })),
        })).sort((a, b) => {
          if (a.id === 'campus-bemke-virtual') return -1;
          if (b.id === 'campus-bemke-virtual') return 1;
          return 0;
        });
      }

      // If filter is active and grouping by org, show Campus Bemke with filtered data
      // and all other organizations with unfiltered data
      if (this.groupByOrg) {
        return this.productsByOrg.map((org) => {
          // Campus Bemke shows filtered products grouped by selected groups
          if (org.id === 'campus-bemke-virtual') {
            const groupMap = {};

            org.orders.forEach((menu) => {
              menu.products.forEach((product) => {
                const groupCodes = Object.keys(product.countsByGroup || {});

                groupCodes.forEach((groupCode) => {
                  if (!this.selectedGroupFilter.includes(groupCode)) return;

                  const subProducts = (product.subProducts || [])
                    .map((subProduct) => {
                      const groupCount = subProduct.countsByGroup?.[groupCode] || 0;
                      if (!groupCount) return null;

                      const delivered = subProduct.deliveredByGroup?.[groupCode] || 0;
                      const previousDayCount = subProduct.previousDayCountsByGroup?.[groupCode] || 0;
                      const lastWeekCount = subProduct.lastWeekCountsByGroup?.[groupCode] || 0;

                      const scale = subProduct.count ? groupCount / subProduct.count : 0;

                      return {
                        ...subProduct,
                        count: groupCount,
                        delivered,
                        previousDayCount,
                        lastWeekCount,
                        ingredients: this.scaleIngredients(subProduct.ingredients, scale),
                        payersByGroup: subProduct.payersByGroup,
                        deliveredPayersByGroup: subProduct.deliveredPayersByGroup,
                        previousDayPayersByGroup: subProduct.previousDayPayersByGroup,
                        lastWeekPayersByGroup: subProduct.lastWeekPayersByGroup,
                      };
                    })
                    .filter((x) => x);

                  if (subProducts.length === 0) return;

                  const count = subProducts.reduce((sum, sp) => sum + (sp.count || 0), 0);
                  const delivered = subProducts.reduce((sum, sp) => sum + (sp.delivered || 0), 0);
                  const previousDayCount = subProducts.reduce((sum, sp) => sum + (sp.previousDayCount || 0), 0);
                  const lastWeekCount = subProducts.reduce((sum, sp) => sum + (sp.lastWeekCount || 0), 0);

                  if (!groupMap[groupCode]) {
                    groupMap[groupCode] = {
                      groupCode,
                      groupName: this.getGroupName(groupCode),
                      products: [],
                    };
                  }

                  groupMap[groupCode].products.push({
                    ...product,
                    menuId: menu.nodeId,
                    menuName: menu.name,
                    count,
                    delivered,
                    previousDayCount,
                    lastWeekCount,
                    subProducts,
                  });
                });
              });
            });

            const groups = Object.values(groupMap).map((group) => {
              const allPayers = new Set();
              const deliveredPayers = new Set();
              const previousDayPayers = new Set();
              const lastWeekPayers = new Set();

              group.products.forEach((product) => {
                product.subProducts?.forEach((sp) => {
                  sp.payersByGroup?.[group.groupCode]?.forEach((payerId) => allPayers.add(payerId));
                  sp.deliveredPayersByGroup?.[group.groupCode]?.forEach((payerId) => deliveredPayers.add(payerId));
                  sp.previousDayPayersByGroup?.[group.groupCode]?.forEach((payerId) => previousDayPayers.add(payerId));
                  sp.lastWeekPayersByGroup?.[group.groupCode]?.forEach((payerId) => lastWeekPayers.add(payerId));
                });
              });

              return {
                ...group,
                products: group.products.sort((a, b) => a.order - b.order),
                totalCount: allPayers.size,
                totalDelivered: deliveredPayers.size,
                totalLeftToDeliver: allPayers.size - deliveredPayers.size,
                totalPreviousDayCount: previousDayPayers.size,
                totalLastWeekCount: lastWeekPayers.size,
              };
            });

            return {
              ...org,
              groups,
            };
          }

          // Other organizations show all their products unfiltered
          return {
            ...org,
            groups: org.orders.map((menu) => ({
              groupCode: null,
              groupName: menu.name,
              products: menu.products.map((product) => ({
                ...product,
                menuId: menu.nodeId,
                menuName: menu.name,
              })),
            })),
          };
        }).sort((a, b) => {
          if (a.id === 'campus-bemke-virtual') return -1;
          if (b.id === 'campus-bemke-virtual') return 1;
          return 0;
        });
      }

      // If filter is active but not grouping by org, show products grouped by selected groups
      return this.productsByOrg
        .map((org) => {
          const groupMap = {};

          org.orders.forEach((menu) => {
            menu.products.forEach((product) => {
              const groupCodes = Object.keys(product.countsByGroup || {});

              groupCodes.forEach((groupCode) => {
                if (!this.selectedGroupFilter.includes(groupCode)) return;

                const subProducts = (product.subProducts || [])
                  .map((subProduct) => {
                    const groupCount = subProduct.countsByGroup?.[groupCode] || 0;
                    if (!groupCount) return null;

                    const delivered = subProduct.deliveredByGroup?.[groupCode] || 0;
                    const previousDayCount = subProduct.previousDayCountsByGroup?.[groupCode] || 0;
                    const lastWeekCount = subProduct.lastWeekCountsByGroup?.[groupCode] || 0;

                    const scale = subProduct.count ? groupCount / subProduct.count : 0;

                    return {
                      ...subProduct,
                      count: groupCount,
                      delivered,
                      previousDayCount,
                      lastWeekCount,
                      ingredients: this.scaleIngredients(subProduct.ingredients, scale),
                    };
                  })
                  .filter((x) => x);

                if (subProducts.length === 0) return;

                const count = subProducts.reduce((sum, sp) => sum + (sp.count || 0), 0);
                const delivered = subProducts.reduce((sum, sp) => sum + (sp.delivered || 0), 0);
                const previousDayCount = subProducts.reduce((sum, sp) => sum + (sp.previousDayCount || 0), 0);
                const lastWeekCount = subProducts.reduce((sum, sp) => sum + (sp.lastWeekCount || 0), 0);

                if (!groupMap[groupCode]) {
                  groupMap[groupCode] = {
                    groupCode,
                    groupName: this.getGroupName(groupCode),
                    products: [],
                  };
                }

                groupMap[groupCode].products.push({
                  ...product,
                  menuId: menu.nodeId,
                  menuName: menu.name,
                  count,
                  delivered,
                  previousDayCount,
                  lastWeekCount,
                  subProducts,
                });
              });
            });
          });

          const groups = Object.values(groupMap).map((group) => {
            const allPayers = new Set();
            const deliveredPayers = new Set();
            const previousDayPayers = new Set();
            const lastWeekPayers = new Set();

            group.products.forEach((product) => {
              product.subProducts?.forEach((sp) => {
                sp.payersByGroup?.[group.groupCode]?.forEach((payerId) => allPayers.add(payerId));
                sp.deliveredPayersByGroup?.[group.groupCode]?.forEach((payerId) => deliveredPayers.add(payerId));
                sp.previousDayPayersByGroup?.[group.groupCode]?.forEach((payerId) => previousDayPayers.add(payerId));
                sp.lastWeekPayersByGroup?.[group.groupCode]?.forEach((payerId) => lastWeekPayers.add(payerId));
              });
            });

            return {
              ...group,
              products: group.products.sort((a, b) => a.order - b.order),
              totalCount: allPayers.size,
              totalDelivered: deliveredPayers.size,
              totalLeftToDeliver: allPayers.size - deliveredPayers.size,
              totalPreviousDayCount: previousDayPayers.size,
              totalLastWeekCount: lastWeekPayers.size,
            };
          });

          return {
            ...org,
            groups,
          };
        })
        .filter((org) => org.groups.length > 0 && org.groups.some((g) => g.products.length > 0));
    },
    dataFreshness() {
      return calculateDataFreshness(this.dataFrom, this.now);
    },
    orderedProducts() {
      if (!this.catalog || !this.orders?.orders) return [];
      const base = transformOrdersToProducts(this.orders.orders, this.listings, this.catalog.metadata);
      return resolveProductIds(base, this.menuNodes, this.diets);
    },
    lastWeekOrderedProducts() {
      if (!this.catalog || !this.lastWeekOrders?.orders) return [];
      const base = transformOrdersToProducts(this.lastWeekOrders.orders, this.listings, this.catalog.metadata);
      return resolveProductIds(base, this.menuNodes, this.diets);
    },
    previousDayOrderedProducts() {
      if (!this.catalog || !this.previousDayOrders?.orders) return [];
      const base = transformOrdersToProducts(this.previousDayOrders.orders, this.listings, this.catalog.metadata);
      return resolveProductIds(base, this.menuNodes, this.diets);
    },
    aggregatedOrders() {
      return this.getAggregatedOrders(
        this.orderedProducts,
        this.lastWeekOrderedProducts,
        this.previousDayOrderedProducts,
      );
    },
    lastWeekdayLabel() {
      const weekday = new Intl.DateTimeFormat('pl-PL', { weekday: 'long' }).format(new Date());
      return `Zeszły(a) ${weekday}`;
    },
  },
  watch: {
    selectedGroupFilter: {
      deep: true,
      handler() {
        this.refreshOrganizationData();
        this.requestOrders();
      },
    },
    groupByOrg(newValue) {
      if (newValue) {
        // When grouping by org is enabled, set specific group filters by finding their codes
        const targetNames = ['Gość', 'Pracownik wewnętrzny', 'Klasy 0 -3', 'Klasy 4 -8'];
        this.selectedGroupFilter = this.groups
          .filter((g) => targetNames.includes(g.name))
          .map((g) => g.code);
      } else {
        // When grouping by org is disabled, clear the filter
        this.selectedGroupFilter = [];
      }
      this.requestOrders();
    },
  },
  methods: {
    ...mapActions(['getPayers', 'getPayersProperties', 'getPayersGroups']),
    ...mapActions('shop', ['getOrders', 'getShopCatalog', 'getDeliveryPlanCalendar', 'getCatalogListings']),
    collapseAll() {
      this.expandAllState = false;
      // Collapse all groups
      this.orgGroupProducts.forEach((org) => {
        org.groups.forEach((group) => {
          if (group.groupCode) {
            this.$set(this.expandedGroups, `${org.id}-${group.groupCode}`, false);
          }
        });
      });
      this.$nextTick(() => {
        this.expandAllState = null;
      });
    },
    expandAll() {
      // First expand all groups
      this.orgGroupProducts.forEach((org) => {
        org.groups.forEach((group) => {
          if (group.groupCode) {
            this.$set(this.expandedGroups, `${org.id}-${group.groupCode}`, true);
          }
        });
      });

      // Then expand all products after groups are rendered
      this.$nextTick(() => {
        this.expandAllState = true;
        this.$nextTick(() => {
          this.expandAllState = null;
        });
      });
    },
    toggleGroup(orgId, groupCode) {
      const key = `${orgId}-${groupCode}`;
      this.$set(this.expandedGroups, key, !this.expandedGroups[key]);
    },
    isGroupExpanded(orgId, groupCode) {
      const key = `${orgId}-${groupCode}`;
      // Default to expanded if not set
      return this.expandedGroups[key] !== false;
    },
    getGroupName(groupCode) {
      const group = this.groups.find((g) => g.code === groupCode);
      return group?.name || groupCode;
    },
    scaleIngredients(ingredients, scale) {
      if (!ingredients || scale === 1) return ingredients;
      const result = {};
      Object.entries(ingredients).forEach(([ingredientId, ingredient]) => {
        const clone = { ...ingredient };
        if (typeof ingredient.weight === 'number') clone.weight = ingredient.weight * scale;
        if (typeof ingredient.deliveredWeight === 'number') clone.deliveredWeight = ingredient.deliveredWeight * scale;
        if (typeof ingredient.count === 'number') clone.count = ingredient.count * scale;
        if (typeof ingredient.deliveredCount === 'number') clone.deliveredCount = ingredient.deliveredCount * scale;
        result[ingredientId] = clone;
      });
      return result;
    },
    attachPayerGroupsToOrders() {
      const annotate = (ordersObj) => {
        if (!ordersObj || !ordersObj.orders) return;
        ordersObj.orders.forEach((o) => {
          const payer = this.payersById[o.payerId];
          const groups = payer?.groups || payer?.groupCodes || [];
          this.$set(o, 'payerGroups', groups.slice ? groups.slice() : groups);
        });
      };

      annotate(this.orders);
      annotate(this.previousDayOrders);
      annotate(this.lastWeekOrders);
    },
    getAggregatedOrders(products, lastWeekProducts, previousDayProducts = []) {
      if (products.length === 0 || !this.meals || !this.ingredients) return [];
      /* eslint-disable no-param-reassign */

      return this.menuNodes
        .map((menu) => {
          const buildGroupCountsMap = (productList = []) =>
            productList
              .filter((x) => x && x.entryMenuId === menu.nodeId)
              .reduce((acc, curr) => {
                const key = `${curr.productId}|${curr.entryDietId}`;
                if (!acc[key]) acc[key] = {};

                (curr.payerGroups || []).forEach((groupCode) => {
                  acc[key][groupCode] = (acc[key][groupCode] || 0) + curr.count;
                });

                return acc;
              }, {});

          const buildGroupPayersMap = (productList = []) =>
            productList
              .filter((x) => x && x.entryMenuId === menu.nodeId)
              .reduce((acc, curr) => {
                const key = `${curr.productId}|${curr.entryDietId}`;
                if (!acc[key]) acc[key] = {};

                (curr.payerGroups || []).forEach((groupCode) => {
                  if (!acc[key][groupCode]) acc[key][groupCode] = new Set();
                  acc[key][groupCode].add(curr.payerId);
                });

                return acc;
              }, {});

          const previousDayGroupCounts = buildGroupCountsMap(previousDayProducts);
          const lastWeekGroupCounts = buildGroupCountsMap(lastWeekProducts);
          const previousDayGroupPayers = buildGroupPayersMap(previousDayProducts);
          const lastWeekGroupPayers = buildGroupPayersMap(lastWeekProducts);

          const menuProducts = products
            .filter((x) => x)
            .filter((x) => x.entryMenuId === menu.nodeId)
            .reduce((acc, curr) => {
              const key = curr.productId;

              const deliveryPlan = this.deliveryPlan[menu.nodeId] || [];
              const deliveryPlanEntry = deliveryPlan.find(
                (x) => x.dietId === curr.entryDietId && x.productId === curr.productId,
              );

              const meal = this.meals.find((x) => x.id === deliveryPlanEntry?.mealId);

              // eslint-disable-next-line operator-linebreak
              const rawIngredients =
                meal?.ingredients.filter(
                  (x) => x.dietId === curr.entryDietId && x.mealSizeId === curr.entryMealSizeId,
                ) || [];

              const ingredients = processIngredients(rawIngredients, curr.count, curr.delivered);

              const lastWeekCount = calculatePeriodCount(lastWeekProducts, menu, curr);
              const previousDayCount = calculatePeriodCount(previousDayProducts, menu, curr);

              if (!acc[key]) {
                const product = this.orders.products.find((p) => p.id === curr.productId);
                acc[key] = createProductEntry(curr.productId, product.name, product.ordinal, meal?.name);
              }

              const existingSubProduct = acc[key].subProducts.find((x) => x.entryDietId === curr.entryDietId);

              if (existingSubProduct) {
                existingSubProduct.count += curr.count;
                existingSubProduct.delivered += curr.delivered;

                // Track counts by group
                const groups = curr.payerGroups || [];
                groups.forEach((groupCode) => {
                  if (!existingSubProduct.countsByGroup) {
                    existingSubProduct.countsByGroup = {};
                  }
                  if (!existingSubProduct.countsByGroup[groupCode]) {
                    existingSubProduct.countsByGroup[groupCode] = 0;
                  }
                  existingSubProduct.countsByGroup[groupCode] += curr.count;

                  if (!existingSubProduct.deliveredByGroup) {
                    existingSubProduct.deliveredByGroup = {};
                  }
                  if (!existingSubProduct.deliveredByGroup[groupCode]) {
                    existingSubProduct.deliveredByGroup[groupCode] = 0;
                  }
                  existingSubProduct.deliveredByGroup[groupCode] += curr.delivered;

                  // Track unique payers by group
                  if (!existingSubProduct.payersByGroup) {
                    existingSubProduct.payersByGroup = {};
                  }
                  if (!existingSubProduct.payersByGroup[groupCode]) {
                    existingSubProduct.payersByGroup[groupCode] = new Set();
                  }
                  existingSubProduct.payersByGroup[groupCode].add(curr.payerId);

                  if (!existingSubProduct.deliveredPayersByGroup) {
                    existingSubProduct.deliveredPayersByGroup = {};
                  }
                  if (!existingSubProduct.deliveredPayersByGroup[groupCode]) {
                    existingSubProduct.deliveredPayersByGroup[groupCode] = new Set();
                  }
                  if (curr.delivered > 0) {
                    existingSubProduct.deliveredPayersByGroup[groupCode].add(curr.payerId);
                  }
                });

                // Merge ingredients
                const newIngredients = mergeIngredients(ingredients, this.ingredients);
                Object.entries(newIngredients).forEach(([ingredientId, ingredient]) => {
                  if (!existingSubProduct.ingredients[ingredientId]) {
                    existingSubProduct.ingredients[ingredientId] = ingredient;
                  } else {
                    mergeExistingIngredient(existingSubProduct.ingredients[ingredientId], ingredient);
                  }
                });
              } else {
                const dietName = this.catalog.subCatalogNodes.find((x) => x.nodeId === curr.entryDietId)?.name || '?';
                const mergedIngredients = mergeIngredients(ingredients, this.ingredients);

                // Initialize counts by group
                const countsByGroup = {};
                const deliveredByGroup = {};
                const payersByGroup = {};
                const deliveredPayersByGroup = {};
                const groups = curr.payerGroups || [];
                groups.forEach((groupCode) => {
                  countsByGroup[groupCode] = curr.count;
                  deliveredByGroup[groupCode] = curr.delivered;
                  payersByGroup[groupCode] = new Set([curr.payerId]);
                  deliveredPayersByGroup[groupCode] = curr.delivered > 0 ? new Set([curr.payerId]) : new Set();
                });

                const groupKey = `${curr.productId}|${curr.entryDietId}`;

                const newSubProduct = createSubProduct(
                  curr.entryDietId,
                  dietName,
                  curr.count,
                  curr.delivered,
                  lastWeekCount,
                  previousDayCount,
                  mergedIngredients,
                );
                newSubProduct.countsByGroup = countsByGroup;
                newSubProduct.deliveredByGroup = deliveredByGroup;
                newSubProduct.payersByGroup = payersByGroup;
                newSubProduct.deliveredPayersByGroup = deliveredPayersByGroup;
                newSubProduct.previousDayCountsByGroup = { ...(previousDayGroupCounts[groupKey] || {}) };
                newSubProduct.lastWeekCountsByGroup = { ...(lastWeekGroupCounts[groupKey] || {}) };
                newSubProduct.previousDayPayersByGroup = {};
                newSubProduct.lastWeekPayersByGroup = {};
                Object.keys(previousDayGroupPayers[groupKey] || {}).forEach((gc) => {
                  newSubProduct.previousDayPayersByGroup[gc] = new Set(previousDayGroupPayers[groupKey][gc]);
                });
                Object.keys(lastWeekGroupPayers[groupKey] || {}).forEach((gc) => {
                  newSubProduct.lastWeekPayersByGroup[gc] = new Set(lastWeekGroupPayers[groupKey][gc]);
                });

                acc[key].subProducts.push(newSubProduct);
              }

              return acc;
            }, {});

          return {
            ...menu,
            products: Object.values(menuProducts)
              .map((product) => {
                const aggregated = aggregateProductCounts(product.subProducts);

                // Aggregate counts by group across all subProducts
                const countsByGroup = {};
                product.subProducts.forEach((subProduct) => {
                  if (subProduct.countsByGroup) {
                    Object.entries(subProduct.countsByGroup).forEach(([groupCode, count]) => {
                      if (!countsByGroup[groupCode]) {
                        countsByGroup[groupCode] = 0;
                      }
                      countsByGroup[groupCode] += count;
                    });
                  }
                });

                return {
                  ...product,
                  ...aggregated,
                  countsByGroup,
                };
              })
              .sort((a, b) => a.order - b.order),
          };
        })
        .filter((x) => x.products.length > 0);

      /* eslint-enable no-param-reassign */
    },
    requestOrders() {
      this.error = false;
      this.pending = true;

      fetchAllOrders(this.getOrders, this.catalogId, this.now)
        .then(({ current, previousDay, lastWeek }) => {
          this.orders = current;
          this.previousDayOrders = previousDay;
          this.lastWeekOrders = lastWeek;
          this.dataFrom = moment(this.now).format();
          this.pending = false;
          // Enrich orders with payer groups if available
          this.attachPayerGroupsToOrders();
        })
        .catch(() => {
          this.pending = false;
          this.error = true;
        });
    },
    async refresh() {
      this.requestOrders();
      this.weekDayName = getWeekdayName(this.now);

      await this.refreshCatalogData();
      await this.refreshMenuData();
      await this.refreshOrganizationData();
      this.refreshDeliveryPlans();
    },
    async refreshCatalogData() {
      try {
        const catalogDetails = await this.getShopCatalog({
          params: {
            catalogId: this.catalogId,
          },
        });

        const listings = await this.getCatalogListings({
          params: {
            catalogId: this.catalogId,
            query: {
              withArchived: true,
            },
          },
        });

        this.listings = listings.data;
        this.catalog = catalogDetails.data;

        const { menuId } = catalogDetails.data.metadata;
        this.menuNodes = catalogDetails.data.subCatalogNodes.filter((x) => x.subCatalogId === menuId);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error refreshing catalog data:', error);
        this.error = true;
      }
    },
    async refreshMenuData() {
      try {
        const { meals, ingredients } = await fetchCatalogMenuData(this.catalogId);
        this.meals = meals;
        this.ingredients = ingredients;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error refreshing menu data:', error);
        this.error = true;
      }
    },
    async refreshOrganizationData() {
      try {
        const { data: properties } = await this.getPayersProperties();
        this.orgCommentPropId = properties.find((x) => x.name.toLowerCase() === 'notatka')?.code;

        // Ensure groups are loaded and compute all group code paths for withGroup params
        await this.getPayersGroups();

        const { data } = await this.getPayers({
          params: {
            query: {
              skip: 0,
              take: -1,
              withProperty: this.orgCommentPropId ? [this.orgCommentPropId] : [],
              withGroup: this.selectedGroupFilter,
            },
          },
        });

        this.orgs = data.filter((x) => x.type !== 'Person');

        // Build quick lookup for annotating orders with payer groups
        const map = {};
        data.forEach((p) => {
          map[p.id] = p;
        });
        this.payersById = map;
        this.attachPayerGroupsToOrders();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error refreshing organization data:', error);
        this.error = true;
      }
    },
    refreshDeliveryPlans() {
      this.menuNodes.forEach((node) => {
        this.getDeliveryPlanCalendar({
          params: {
            catalogId: this.catalogId,
            menuNodeId: node.nodeId,
            query: {
              from: moment(this.now).format('YYYY-MM-DD'),
              to: moment(this.now).add(1, 'day').format('YYYY-MM-DD'),
            },
          },
        }).then(({ data }) => {
          this.$set(this.deliveryPlan, node.nodeId, data);
        });
      });
    },
  },
  created() {
    this.$store.commit('setPayeeId', this.payeeId);
    this.now = moment().format();
    this.refresh();

    this.nowInterval = setInterval(() => {
      this.now = moment().format();
      this.refresh();
    }, 20_000);
  },
  destroyed() {
    if (this.nowInterval) {
      clearInterval(this.nowInterval);
    }
  },
};
</script>

<style lang="scss" scoped>
.menu-name {
  font-size: 20px;
  font-weight: 500;
}

.org-section {
  margin-bottom: 24px;
}

.group-section {
  margin-top: 12px;
  padding: 8px 12px;
  background: #F5F8FF;
  border-radius: 6px;
}

.group-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  margin: 10px 0;
}

.group-name {
  font-size: 26px;
  font-weight: 600;
}

.group-number {
  width: 200px;
  text-align: right;
}

.group-number-label {
  font-size: 14px;
  line-height: 120%;
  font-weight: 500;
  color: #000000;
  text-align: right;
}

.dimmed {
  color: #666 !important;

  .group-number-label {
    font-size: 12px;
    color: #666 !important;
  }

  .group-number-value {
    font-size: 18px;
    font-weight: 500;
    color: #666 !important;
  }
}

.group-number-value {
  font-size: 24px;
  line-height: 100%;
  font-weight: 500;
  color: #000000;
  text-align: right;
}

.group-number-value-past {
  font-size: 20px;
  line-height: 100%;
  font-weight: 500;
  color: #000000;
  text-align: right;
}

.group-chevron-spacer {
  width: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: #666;
  margin-left: 16px;
  margin-right: 15px;
}

.payer-icon {
  color: $light-gray;
}

.full-height {
  height: calc(100vh - 100px);
}

@media screen and (max-width: 991px) {
  .full-height {
    height: calc(100vh - 150px);
  }
}

.wrapper {
  min-height: 100vh;
  background-color: #efefef;
}

.content {
  padding: 0 20px 20px;
}
</style>
